import { resources } from "@constants/hardcoded-strings";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { UpdateOrganizationDetailsStepTypes } from "@enums/update-organization-details-step-types";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import { OrganizationConfig } from "@idw-types/data/organization-config";
import { RpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { updateOrganizationDetailsReduxRequest } from "@redux-requests/organization-details";
import { updateAxnAdminUsersCredentialsRequest } from "@requests/axnadmin-core/update-axn-admin-users-credentials";
import { createVerificationServiceChangeRequestRequest, getRpServiceChangeRequest, updateVerificationServiceDetailsRequest } from "@requests/service-configuration";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { getRemainingChangeRequestStatuses } from "@utils/change-request-modal-util/common-change-request-modal-util";
import { deployVerificationServiceInOrder } from "@utils/change-request-modal-util/verification-service-change-request-modal-util";
import { Dispatch } from "redux";

export type UpdateFullOrganizationDetailsResponse = {
    failedStep: UpdateOrganizationDetailsStepTypes;
    axnAdminUsersNotUpdated?: Array<AxnAdminUser>;
    axnAdminUsersUpdated?: Array<AxnAdminUser>;
    verificationServiceDetails?: RpServiceConfiguration;
}

type UpdateAxnAdminUsersResponse = { failedStep: UpdateOrganizationDetailsStepTypes; } & {
    axnAdminUsersNotUpdated?: Array<AxnAdminUser>;
    axnAdminUsersUpdated?: Array<AxnAdminUser>;
}

const getRemainingUpdateOrganizationDetailSteps = (
    currentUpdateOrganizationDetailsStep: UpdateOrganizationDetailsStepTypes,
    hasIDPChanged: boolean,
    organizationUsers: Array<AxnAdminUser>
) => {
    const steps = [UpdateOrganizationDetailsStepTypes.UPDATE_ORG_DETAILS];
    if (hasIDPChanged) {
        steps.push(
            UpdateOrganizationDetailsStepTypes.UPDATE_CORPORATE_LOGIN_SERVICE_DETAILS_ASSOCIATED,
            UpdateOrganizationDetailsStepTypes.DEPLOY_CORPORATE_LOGIN_SERVICE_DETAILS_ASSOCIATED
        );

        if (organizationUsers.length > 0) {
            steps.push(UpdateOrganizationDetailsStepTypes.UPDATE_ORGANIZATION_USERS_CREDENTIALS);
        }
    }

    let currentStepIndex: number = null;

    const remainingSteps = steps.filter((status, index) => {
        if (currentUpdateOrganizationDetailsStep === null) {
            currentStepIndex = -1;
        }

        if (status === currentUpdateOrganizationDetailsStep) {
            currentStepIndex = index;
        }

        if (currentStepIndex !== null && index >= currentStepIndex) {
            return true;
        }

        return false;
    });

    return remainingSteps;
};

const updateOrganizationDetails = (dispatch: Dispatch) => async (organizationConfigState: OrganizationConfig, shouldUpdateOrganizationConfig: boolean): Promise<UpdateFullOrganizationDetailsResponse> => {
    if (shouldUpdateOrganizationConfig) {
        const response = await updateOrganizationDetailsReduxRequest(dispatch)(organizationConfigState);
        if (response) {
            return {
                failedStep: null
            }
        } else {
            return { failedStep: UpdateOrganizationDetailsStepTypes.UPDATE_ORG_DETAILS };
        }
    } else {
        return {
            failedStep: null
        }
    }
}

export const updateVerificationServiceDetails = (dispatch: Dispatch) =>
    async (rpServiceConfiguration: RpServiceConfiguration, loginServiceIDPID: string): Promise<UpdateFullOrganizationDetailsResponse> => {
        const result: UpdateFullOrganizationDetailsResponse = {
            failedStep: null,
            verificationServiceDetails: null
        };

        const finalRpServiceConfiguration: RpServiceConfiguration = {
            ...rpServiceConfiguration,
            idps: [{
                identityProviderID: loginServiceIDPID,
                id: undefined,
                rpServiceID: undefined
            }]
        };

        // Get the current change Request 
        const rpServiceChangeRequestResponse = await getRpServiceChangeRequest(rpServiceConfiguration.id);

        if (rpServiceChangeRequestResponse?.data) {
            let currentServiceDetailsChangeRequestStatus = rpServiceChangeRequestResponse.data.rpServiceChangeRequests.length === 0
                ? ChangeRequestStatusTypes.DEPLOYED
                : rpServiceChangeRequestResponse.data.rpServiceChangeRequests[0].changeRequestStatus;

            // if the current change request is DEPLOYED create a change request and edit the service, and deploy it
            // if the current change request is UNSUBMITTED edit the service and deploy the service
            if (currentServiceDetailsChangeRequestStatus === ChangeRequestStatusTypes.DEPLOYED) {
                // Create change request 
                const { data } = await createVerificationServiceChangeRequestRequest(rpServiceConfiguration.id);
                if (data?.changeRequest) {
                    currentServiceDetailsChangeRequestStatus = ChangeRequestStatusTypes.UNSUBMITTED;
                }
            }

            const updateVerificationServiceDetailsResponse = await updateVerificationServiceDetailsRequest(rpServiceConfiguration.id, finalRpServiceConfiguration);
            if (updateVerificationServiceDetailsResponse?.data) {
                result.verificationServiceDetails = updateVerificationServiceDetailsResponse.data.transferObject;
                displaySuccessModal(
                    dispatch,
                    resources.requests.updateVerificationServiceDetails.success.title,
                    resources.requests.updateVerificationServiceDetails.success.message
                );

                return result;

            } else if (updateVerificationServiceDetailsResponse?.error) {
                logAndDisplayErrorModal(
                    dispatch,
                    updateVerificationServiceDetailsResponse.error,
                    resources.requests.updateVerificationServiceDetails.error.title,
                    resources.requests.updateVerificationServiceDetails.error.message,
                    updateVerificationServiceDetailsResponse.error,
                    null,
                    updateVerificationServiceDetailsResponse.body
                );
                result.failedStep = UpdateOrganizationDetailsStepTypes.UPDATE_CORPORATE_LOGIN_SERVICE_DETAILS_ASSOCIATED;
            }
        }

        return result;
    }

export const deployCorporateLoginVerificationServiceDetailsAssociated = (dispatch: Dispatch) => async (
    verificationServiceDetailsId: string): Promise<UpdateFullOrganizationDetailsResponse> => {

    const rpServiceChangeRequestResponse = await getRpServiceChangeRequest(verificationServiceDetailsId);

    if (rpServiceChangeRequestResponse?.data) {
        const currentServiceDetailsChangeRequestStatus = rpServiceChangeRequestResponse.data.rpServiceChangeRequests.length === 0
            ? ChangeRequestStatusTypes.DEPLOYED
            : rpServiceChangeRequestResponse.data.rpServiceChangeRequests[0].changeRequestStatus;

        const remainingChangeRequestStatuses = getRemainingChangeRequestStatuses(currentServiceDetailsChangeRequestStatus);

        const response = await deployVerificationServiceInOrder(
            dispatch,
            verificationServiceDetailsId,
            remainingChangeRequestStatuses
        );

        if (response?.done) {
            displaySuccessModal(
                dispatch,
                resources.requests.deployVerificationServiceChangeRequest.success.title,
                resources.requests.deployVerificationServiceChangeRequest.success.message
            );

            return {
                failedStep: null
            }
        }
    }

    return { failedStep: UpdateOrganizationDetailsStepTypes.DEPLOY_CORPORATE_LOGIN_SERVICE_DETAILS_ASSOCIATED };
}

const updateAxnAdminUsers = (dispatch: Dispatch) => async (organizationUsers: Array<AxnAdminUser>, loginServiceIDPID: string): Promise<UpdateAxnAdminUsersResponse> => {
    const result: UpdateAxnAdminUsersResponse = {
        failedStep: null,
        axnAdminUsersNotUpdated: [],
        axnAdminUsersUpdated: []
    };

    if (organizationUsers.length > 0) {
        const response = await updateAxnAdminUsersCredentialsRequest(dispatch)(organizationUsers, loginServiceIDPID);
        if (response.usersNotUpdated.length === 0) {
            result.axnAdminUsersUpdated = response.userUpdated;
            displaySuccessModal(
                dispatch,
                resources.requests.updateOrganizationUsersCredentials.success.title,
                resources.requests.updateOrganizationUsersCredentials.success.message
            );

            return result;
        } else {
            result.failedStep = UpdateOrganizationDetailsStepTypes.UPDATE_ORGANIZATION_USERS_CREDENTIALS;
            result.axnAdminUsersNotUpdated = response.usersNotUpdated;
            return result;
        }
    } else {
        return result;
    }
}

export const updateFullOrganizationDetailsRequestByStep = (dispatch: Dispatch) => async (
    updateOrganizationDetailStep: UpdateOrganizationDetailsStepTypes,
    organizationConfigState: OrganizationConfig,
    initialOrganizationConfig: OrganizationConfig,
    verificationServiceDetails: RpServiceConfiguration,
    hasIDPChanged: boolean,
    organizationUsers: Array<AxnAdminUser>
): Promise<UpdateFullOrganizationDetailsResponse> => {

    const shouldUpdateOrganizationConfig = (
        initialOrganizationConfig.name !== organizationConfigState.name ||
        initialOrganizationConfig.customerSupportTierUID !== organizationConfigState.customerSupportTierUID ||
        initialOrganizationConfig.companyCode !== organizationConfigState.companyCode
    );
    const remainingSteps: Array<UpdateOrganizationDetailsStepTypes> = getRemainingUpdateOrganizationDetailSteps(
        updateOrganizationDetailStep,
        hasIDPChanged,
        organizationUsers
    );

    let finalResponse: UpdateAxnAdminUsersResponse = {
        failedStep: null,
        axnAdminUsersNotUpdated: [],
        axnAdminUsersUpdated: []
    };


    for (const step of remainingSteps) {
        let currentResponse: UpdateAxnAdminUsersResponse = {
            failedStep: null,
            axnAdminUsersNotUpdated: [],
            axnAdminUsersUpdated: []
        };

        if (step === UpdateOrganizationDetailsStepTypes.UPDATE_ORG_DETAILS) {
            currentResponse = await updateOrganizationDetails(dispatch)(organizationConfigState, shouldUpdateOrganizationConfig)
        } else if (step === UpdateOrganizationDetailsStepTypes.UPDATE_CORPORATE_LOGIN_SERVICE_DETAILS_ASSOCIATED) {
            currentResponse = await updateVerificationServiceDetails(dispatch)(verificationServiceDetails, organizationConfigState.loginServiceIDPID);
        } else if (step === UpdateOrganizationDetailsStepTypes.DEPLOY_CORPORATE_LOGIN_SERVICE_DETAILS_ASSOCIATED) {
            currentResponse = await deployCorporateLoginVerificationServiceDetailsAssociated(dispatch)(verificationServiceDetails.id);
        } else if (step === UpdateOrganizationDetailsStepTypes.UPDATE_ORGANIZATION_USERS_CREDENTIALS) {
            currentResponse = await updateAxnAdminUsers(dispatch)(organizationUsers, organizationConfigState.loginServiceIDPID);
        }

        if (currentResponse && currentResponse.failedStep === null) {
            finalResponse = { ...finalResponse, ...currentResponse };
        } else {
            break;
        }
    }

    return finalResponse;
};